// ============================================================
// ServiceNotificationsPage.js
// Route /platform/service-notifications — the A2Z IT broadcast
// (RULED 2026-08-13: BELL ONLY in v1, audience = managers + admins
// of all clients or a picked subset; no email — a mis-send must
// never email every client).
//
// Compose flow: template → placeholder inputs → scope → PREVIEW
// (a server dryRun — the preview is byte-identical to the send by
// construction, the F7 discipline) → Send behind a ConfirmDialog
// naming the recipient count. Send stays DISABLED until a preview
// of the CURRENT inputs exists: any edit invalidates it, so what
// was previewed is always what sends.
// ============================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import ConfirmDialog from '../components/ConfirmDialog';
import { toast } from '../components/Toaster';
import {
  listServiceNotificationTemplates,
  sendServiceNotification,
  listServiceNotificationHistory,
} from '../api/adminPlatform';
import { listAdminClients } from '../api/adminClients';
import { translateError } from '../utils/errorMessages';
import { formatDate } from '../utils/format';
import './PlatformPages.css';
import './ServiceNotificationsPage.css';

// Placeholder → input treatment. Anything unlisted renders as text.
const PLACEHOLDER_INPUTS = {
  WINDOW_START: { label: 'Window start', type: 'datetime-local' },
  WINDOW_END: { label: 'Window end', type: 'datetime-local' },
  MESSAGE: { label: 'Message', type: 'textarea' },
  AFFECTED_SERVICES: { label: 'Affected services', type: 'text' },
};

function placeholderMeta(key) {
  return PLACEHOLDER_INPUTS[key] || { label: key.replace(/_/g, ' ').toLowerCase(), type: 'text' };
}

export default function ServiceNotificationsPage() {
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyTruncated, setHistoryTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [templateCode, setTemplateCode] = useState('');
  const [variables, setVariables] = useState({});
  const [scopeType, setScopeType] = useState('all');
  const [scopeClientIds, setScopeClientIds] = useState([]);

  // The dryRun result for the CURRENT inputs; null = not previewed yet.
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.code === templateCode) || null,
    [templates, templateCode],
  );

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [tplRes, clientRes, histRes] = await Promise.all([
        listServiceNotificationTemplates(),
        listAdminClients({ pageSize: 200 }),
        listServiceNotificationHistory(),
      ]);
      setTemplates(tplRes.items || []);
      // { items } — the listAdminClients wire shape (verified live
      // 2026-08-14; the first cut read a nonexistent `clients` key and
      // rendered an empty picker).
      setClients((clientRes.items || []).filter((c) => c.isActive));
      setHistory(histRes.items || []);
      setHistoryTruncated(!!histRes.truncated);
    } catch (err) { setError(translateError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Any input change invalidates the preview — what was previewed must
  // always be what sends.
  function invalidatePreview() { setPreview(null); }

  function onTemplateChange(code) {
    setTemplateCode(code);
    setVariables({});
    invalidatePreview();
  }

  function setVariable(key, value) {
    setVariables((prev) => ({ ...prev, [key]: value }));
    invalidatePreview();
  }

  function toggleScopeClient(clientId) {
    setScopeClientIds((prev) => (
      prev.includes(clientId) ? prev.filter((id) => id !== clientId) : [...prev, clientId]
    ));
    invalidatePreview();
  }

  const composeValid = !!selectedTemplate
    && (scopeType === 'all' || scopeClientIds.length > 0)
    && (selectedTemplate.placeholders || []).every((p) => (variables[p] || '').trim() !== '');

  async function runPreview() {
    if (!composeValid) return;
    setPreviewing(true);
    try {
      const res = await sendServiceNotification({
        templateCode,
        variables,
        scopeType,
        scopeClientIds: scopeType === 'clients' ? scopeClientIds : undefined,
        dryRun: true,
      });
      setPreview(res);
    } catch (err) {
      toast.error(translateError(err));
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  }

  async function doSend() {
    setSending(true);
    try {
      const res = await sendServiceNotification({
        templateCode,
        variables,
        scopeType,
        scopeClientIds: scopeType === 'clients' ? scopeClientIds : undefined,
      });
      toast.success(`Service notification sent to ${res.recipientCount} ${res.recipientCount === 1 ? 'person' : 'people'}.`);
      setConfirmOpen(false);
      setPreview(null);
      setVariables({});
      setTemplateCode('');
      await fetchAll();
    } catch (err) {
      toast.error(translateError(err));
    } finally {
      setSending(false);
    }
  }

  const historyColumns = [
    { key: 'sentAt', label: 'Sent', width: '170px', render: (row) => formatDate(row.sentAt) },
    {
      key: 'subject',
      label: 'Notice',
      render: (row) => (
        <div>
          <div>{row.subject}</div>
          <div className="svcnotif__history-body">{row.body}</div>
        </div>
      ),
    },
    {
      key: 'scope',
      label: 'Audience',
      width: '160px',
      render: (row) => (row.scopeType === 'all'
        ? 'All clients'
        : `Clients: ${row.scopeClientIds || ''}`),
    },
    { key: 'recipients', label: 'Recipients', width: '100px', render: (row) => row.recipientCount },
    { key: 'sentBy', label: 'Sent by', width: '220px', render: (row) => row.sentBy },
  ];

  return (
    <div className="platform-page">
      <PageHeader
        title="Service Notifications"
        subtitle="Broadcast a platform notice (maintenance, outage, release) to the bell for managers and admins of all clients or a picked subset. In-app only - no email is sent."
      />

      <section className="svcnotif__compose" aria-label="Compose a service notification">
        <h2 className="svcnotif__heading">Compose</h2>

        <label className="svcnotif__field">
          <span className="svcnotif__label">Template</span>
          <select
            value={templateCode}
            onChange={(e) => onTemplateChange(e.target.value)}
          >
            <option value="">Choose a template…</option>
            {templates.map((t) => (
              <option key={t.code} value={t.code}>{t.name}</option>
            ))}
          </select>
        </label>

        {selectedTemplate && (selectedTemplate.placeholders || []).map((p) => {
          const meta = placeholderMeta(p);
          return (
            <label key={p} className="svcnotif__field">
              <span className="svcnotif__label">{meta.label}</span>
              {meta.type === 'textarea' ? (
                <textarea
                  rows={3}
                  value={variables[p] || ''}
                  onChange={(e) => setVariable(p, e.target.value)}
                />
              ) : (
                <input
                  type={meta.type}
                  value={variables[p] || ''}
                  onChange={(e) => setVariable(p, e.target.value)}
                />
              )}
            </label>
          );
        })}

        <fieldset className="svcnotif__scope">
          <legend className="svcnotif__label">Audience</legend>
          <label className="svcnotif__radio">
            <input
              type="radio"
              name="svcnotif-scope"
              checked={scopeType === 'all'}
              onChange={() => { setScopeType('all'); invalidatePreview(); }}
            />
            All clients
          </label>
          <label className="svcnotif__radio">
            <input
              type="radio"
              name="svcnotif-scope"
              checked={scopeType === 'clients'}
              onChange={() => { setScopeType('clients'); invalidatePreview(); }}
            />
            Picked clients only
          </label>
          {scopeType === 'clients' && (
            <div className="svcnotif__clients">
              {clients.map((c) => (
                <label key={c.clientId} className="svcnotif__client">
                  <input
                    type="checkbox"
                    checked={scopeClientIds.includes(c.clientId)}
                    onChange={() => toggleScopeClient(c.clientId)}
                  />
                  {c.displayName || c.clientName}
                </label>
              ))}
            </div>
          )}
        </fieldset>

        <div className="svcnotif__actions">
          <button
            type="button"
            className="button-secondary"
            disabled={!composeValid || previewing}
            onClick={runPreview}
          >
            {previewing ? 'Previewing…' : 'Preview'}
          </button>
          <button
            type="button"
            className="button-primary"
            disabled={!preview || sending}
            onClick={() => setConfirmOpen(true)}
          >
            Send
          </button>
          {!preview && (
            <span className="svcnotif__hint">Preview first - the preview is exactly what will be sent.</span>
          )}
        </div>

        {preview && (
          <div className="svcnotif__preview" data-testid="svcnotif-preview">
            <div className="svcnotif__preview-subject">{preview.subject}</div>
            <div className="svcnotif__preview-body">{preview.body}</div>
            <div className="svcnotif__preview-meta">
              Will reach <strong>{preview.recipientCount}</strong>{' '}
              {preview.recipientCount === 1 ? 'person' : 'people'} (managers and admins
              {scopeType === 'all' ? ' across all active clients' : ' of the picked clients'}).
            </div>
          </div>
        )}
      </section>

      <section aria-label="Previous service notifications">
        <h2 className="svcnotif__heading">History</h2>
        {historyTruncated && (
          <p className="svcnotif__truncated">Showing the most recent sends only - older entries are not listed.</p>
        )}
        <DataTable
          data={history}
          columns={historyColumns}
          rowKey={(row) => row.logId}
          loading={loading}
          error={error}
          onRetry={fetchAll}
          totalCount={history.length}
          page={1}
          pageSize={history.length || 1}
          onPageChange={() => {}}
          emptyMessage="No service notifications have been sent yet."
        />
      </section>

      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={doSend}
        isSubmitting={sending}
        title="Send this service notification?"
        confirmLabel="Yes, send"
        message={preview
          ? `"${preview.subject}" will appear in the bell for ${preview.recipientCount} ${preview.recipientCount === 1 ? 'person' : 'people'}. This cannot be recalled.`
          : ''}
      />
    </div>
  );
}
